import VideoPlayer from './VideoPlayer.js';

class HtmlPlayer extends VideoPlayer {
  _loadApi() {
    return import(/* webpackChunkName: "video.js" */ 'video.js').then(
      (player) => {
        window.videojs = player.default;
      }
    );
  }

  _apiLoaded() {
    return !!window.videojs;
  }

  _bindApi() {
    this.id = `video${this.id}`;
    let player = (this.player = videojs(this.id));

    player.on('loadedmetadata', () => {
      this.trigger(this.ready_event);
    });

    player.on('ended', () => {
      this.stop();
    });

    this.setStart();

    player.on('timeupdate', () => {
      this.setLatestPlayTime(this.player.currentTime());
    });

    player.on('playbackrateschange', () => {
      // Playback rate changes are handled by ratechange event
    });

    player.on('ratechange', () => {
      this.savePlaybackRate(this.player.playbackRate() || 1);
    });

    this.once('ready', () => {
      this._seek(this.latestPlayTime);
    });
  }

  _changePlaybackRate(speed = 1) {
    this.player.playbackRate(speed);
  }

  _duration() {
    return this.player.duration();
  }

  _play() {
    this.player.play();
  }

  _pause() {
    this.player.pause();
  }

  _stop() {
    this._pause();
  }

  _seek(seconds) {
    this.player.currentTime(seconds);
  }

  buildHtml() {
    let type = /\.m3u8/.test(this.url) ? 'application/x-mpegURL' : 'video/mp4';
    let preload = this.opts.preload || 'auto';

    // Desktop: allow HTML5 autoplay
    // Mobile: no autoplay attribute, but start muted to prepare for gesture
    let autoplayAttr = '';
    let mutedAttr = '';

    if (this.opts.autoplay) {
      if (!this.isMobile()) {
        // Desktop: autoplay unmuted
        autoplayAttr = ' autoplay';
      } else {
        // Mobile: start muted, ready for gesture to unmute and play
        mutedAttr = ' muted';
      }
    }

    return `<video 
              class="video-js" 
              id="video${this.id}" 
              controls 
              preload="${preload}" 
              ${autoplayAttr}
              ${mutedAttr}
              playsinline 
              data-setup='{ "playbackRates": [0.5, 1, 1.5, 2] }'
            >
              <source src="${this.url}" type="${type}"/>
            </video>`;
  }

  wireEvents() {
    if (this.opts?.autoplay && this.isMobile() && !this.gestureArmed) {
      this._armGestureAutoplay();
    }
  }

  // Play video with sound unmuted
  async _playWithSound() {
    try {
      // Unmute and set volume (video.js API)
      this.player.muted(false);
      this.player.volume(1);

      // Play using video.js API (returns a promise)
      await this.player.play();
    } catch (error) {
      console.error('[HtmlPlayer] Error in _playWithSound:', error);
    }
  }

  // Helper method to wait for player to be ready (similar to YouTube/Wistia/Vimeo)
  _ensureReady(callback, attempts = 0) {
    const maxAttempts = 100; // 20 seconds max

    if (this.ready && this.player && typeof this.player.play === 'function') {
      return callback();
    }

    if (attempts >= maxAttempts) {
      console.warn('[HtmlPlayer] Player ready timeout');
      return callback(); // Try anyway
    }

    setTimeout(() => {
      this._ensureReady(callback, attempts + 1);
    }, 200);
  }

  // Override base class method for HTML5 video-specific autoplay logic
  async _handleGesturePlay() {
    // Wait for player to be ready (within gesture context)
    await new Promise((resolve) => {
      this._ensureReady(resolve);
    });

    // Now player is ready, play with sound
    await this._playWithSound();
  }
}

let htmlPlayerRegex = /\.(mp4|m3u8)$/;
HtmlPlayer.test = function (url) {
  if (url.indexOf('cdn.filestack') > 0) {
    return true;
  }

  return url.match(htmlPlayerRegex);
};

export default HtmlPlayer;
